<template lang="pug">
#comments.thread-comments.loading(v-if='loaded === false')
  commentPlaceholder(v-for='num in placeholderCount', :key='num')
#comments.thread-comments.loaded(v-else)
  .thread-comments-more(v-show='!loadedComment')
    .thread-comments-more__inner
      .thread-comments-more__action
        button.a-button.is-lg.is-text.is-block(@click='showComments')
          | 前のコメント（ {{ nextCommentAmount }} ）
  header.thread-comments__header
    h2.thread-comments__title(
      v-if='commentableType === "RegularEvent" || commentableType === "Event"')
      | 質問・連絡・コメント
    h2.thread-comments__title(
      v-else-if='commentableType === "Announcement" || commentableType === "Page"')
      | 質問・コメント
    h2.thread-comments__title(v-else-if='commentableType === "Talk"')
      | 連絡・返信
    h2.thread-comments__title(v-else)
      | コメント
  .thread-comments__items
    comment(
      v-for='(comment, index) in comments',
      :key='comment.id',
      :comment='comment',
      :currentUser='currentUser',
      :id='"comment_" + comment.id',
      :isLatest='index === comments.length - 1',
      @delete='deleteComment',
      @update='updateComment')
  .thread-comment-form(v-if='currentUser.writable')
    #latest-comment(v-if='comments.length === 0')
    .thread-comment__start
      span(:class='["a-user-role", roleClass]')
        img.thread-comment__user-icon.a-user-icon(
          :src='currentUser.avatar_url',
          :title='currentUser.icon_title')
    .thread-comment__end
      .thread-comment-form__form.a-card
        .a-form-tabs.js-tabs
          .a-form-tabs__tab.js-tabs__tab(
            :class='{ "is-active": isActive("comment") }',
            @click='changeActiveTab("comment")')
            | コメント
          .a-form-tabs__tab.js-tabs__tab(
            :class='{ "is-active": isActive("preview") }',
            @click='changeActiveTab("preview")')
            | プレビュー
        .a-markdown-input.js-markdown-parent
          .a-markdown-input__inner.js-tabs__content(
            :class='{ "is-active": isActive("comment") }')
            .form-textarea
              .form-textarea__body
                textarea#js-new-comment.a-text-input.js-warning-form.a-markdown-input__textarea(
                  v-model='description',
                  ref='newComment',
                  name='new_comment[description]',
                  data-preview='#new-comment-preview',
                  data-input='.new-comment-file-input',
                  @input='editComment')
              .form-textarea__footer
                .form-textarea__insert
                  label.a-file-insert.a-button.is-xs.is-text-reversal.is-block
                    | ファイルを挿入
                    input.new-comment-file-input(type='file', multiple)

                // 提出物 かつ 講師権限 かつ 評価基準がある場合、アクションボタンを表示する
                .form-textarea__tags(v-if='commentableType === "Product" && currentUser.operator && hasEvaluationCriteria')
                  label.a-button.is-xs.is-text-reversal.is-block(
                    @click="setTemplate",
                    :data-message="generateCorrectionMessage()")
                    | 修正点あり
                  label.a-button.is-xs.is-text-reversal.is-block(
                    @click="setTemplate",
                    :data-message="generateEvaluationMessage()")
                    | 改善点あり
                  label.a-button.is-xs.is-text-reversal.is-block(
                    @click="setTemplate",
                    :data-message="generatePerfectionMessage()")
                    | 改善点なし

                // 相談部屋 かつ 研修生の場合、アクションボタンを表示する
                .form-textarea__tags(v-if='commentableType === "Talk" && currentUser.primary_role === "trainee"')
                  label.a-button.is-xs.is-text-reversal.is-block(
                    @click="setTemplate",
                    :data-message="lateMessageTemplate")
                    | 遅刻
                  label.a-button.is-xs.is-text-reversal.is-block(
                      @click="setTemplate",
                      :data-message="leaveEarlyMessageTemplate")
                    | 早退
                  label.a-button.is-xs.is-text-reversal.is-block(
                      @click="setTemplate",
                      :data-message="dayOffMessageTemplate")
                    | 休暇
          .a-markdown-input__inner.js-tabs__content(
            :class='{ "is-active": isActive("preview") }')
            #new-comment-preview.a-long-text.is-md.a-markdown-input__preview
        .card-footer
          .card-main-actions
            .card-main-actions__items
              .card-main-actions__item
                button#js-shortcut-post-comment.a-button.is-sm.is-primary.is-block(
                  @click='postComment',
                  :disabled='!validation || buttonDisabled')
                  | コメント
              //.card-main-actions__item.is-only-mentor(
                v-if='isRole("mentor") && commentType && !checkId')
                button.a-button.is-sm.is-danger.is-block(
                  @click='commentAndCheck',
                  :disabled='!validation || buttonDisabled')
                  i.fa-solid.fa-check
                  | 確認OKにする
</template>
<script>
import CSRF from 'csrf'
import Comment from 'comment.vue'
import TextareaInitializer from 'textarea-initializer'
import CommentPlaceholder from 'comment-placeholder'
import confirmUnload from 'confirm-unload'
import toast from 'toast'
import role from 'role'
import checkable from 'checkable.js'
import ImageModalViewer from 'image-modal-viewer'

export default {
  components: {
    comment: Comment,
    commentPlaceholder: CommentPlaceholder
  },
  mixins: [toast, confirmUnload, role, checkable],
  props: {
    commentableId: { type: String, required: true },
    commentableType: { type: String, required: true },
    currentUserId: { type: Number, required: true },
    currentUser: { type: Object, required: true },
    practice: { type: Object, required: false },
  },
  data() {
    return {
      comments: [],
      description: '',
      tab: 'comment',
      buttonDisabled: false,
      defaultTextareaSize: null,
      loaded: false,
      editing: false,
      placeholderCount: 3,
      commentLimit: 8,
      commentOffset: 0,
      commentTotalCount: null,
      loadedComment: false,
      nextCommentAmount: null,
      incrementCommentSize: 8
    }
  },
  computed: {
    validation() {
      return this.description.length > 0
    },
    commentType() {
      return /^(Report|Product)$/.test(this.commentableType)
    },
    checkId() {
      return this.$store.getters.checkId
    },
    roleClass() {
      return `is-${this.currentUser.primary_role}`
    },
    productCheckerId() {
      return this.$store.getters.productCheckerId
    },
    today() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}/${month}/${day}`;
    },
    // 評価基準が存在するかどうかを判定するプロパティを追加
    hasEvaluationCriteria() {
      // 1. this.practice?.description で安全にdescriptionを取得
      // ※ ?. は practice が null や undefined でもエラーにならないための記法
      const description = this.practice?.description;
      if (!description) {
        return false;
      }
      // 2. 評価の見出しがあるか正規表現でテストする
      const headingRegex = /^##\s*.*評価.*$/m;
      return headingRegex.test(description);
    },
    lateMessageTemplate() {
      return `名前：${this.currentUser.name}
対象日：${this.today}
区分：遅刻
出社予定時刻：XX:XX
事由：〇〇〇〇〇〇`
    },
    leaveEarlyMessageTemplate() {
      return `名前：${this.currentUser.name}
対象日：${this.today}
区分：早退
早退時刻：XX:XX
事由：〇〇〇〇〇〇`
    },
    dayOffMessageTemplate() {
      return `名前：${this.currentUser.name}
対象日：${this.today}
区分：休暇
事由：〇〇〇〇〇〇`
    }
  },
  created() {
    this.showComments()
  },
  updated() {
    // 非同期でDOM追加後、画像モーダル初期化イベントを実行
    const imageModalViewer = new ImageModalViewer()
    imageModalViewer.initialize()
  },
  methods: {
    isActive(tab) {
      return this.tab === tab
    },
    changeActiveTab(tab) {
      this.tab = tab
    },
    displayMoreComments() {
      this.loadedComment =
        this.commentLimit + this.commentOffset >= this.commentTotalCount
      if (!this.loadedComment) {
        this.commentOffset += this.commentLimit
      }
      const commentRemaining = this.commentTotalCount - this.commentOffset

      if (commentRemaining > this.incrementCommentSize) {
        this.nextCommentAmount = `${this.incrementCommentSize} / ${commentRemaining}`
      } else {
        this.nextCommentAmount = commentRemaining
      }
    },
    showComments() {
      fetch(
        `/api/comments.json?commentable_type=${this.commentableType}&` +
          `commentable_id=${this.commentableId}&comment_limit=${this.commentLimit}&` +
          `comment_offset=${this.commentOffset}`,
        {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          redirect: 'manual'
        }
      )
        .then((response) => {
          return response.json()
        })
        .then((json) => {
          json.comments.forEach((c) => {
            this.comments.unshift(c)
          })
          this.commentTotalCount = json.comment_total_count
          this.displayMoreComments()
        })
        .catch((error) => {
          console.warn(error)
        })
        .finally(() => {
          if (this.loaded === false) {
            this.loaded = true
            this.$nextTick(() => {
              TextareaInitializer.initialize('#js-new-comment')
              this.setDefaultTextareaSize()
              Prism.highlightAll()
            })
          }
        })
    },
    createComment({ toastMessage } = {}) {
      if (this.description.length < 1) {
        return null
      }
      this.buttonDisabled = true
      this.editing = false
      const params = {
        comment: { description: this.description },
        commentable_type: this.commentableType,
        commentable_id: this.commentableId
      }
      fetch(`/api/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': CSRF.getToken()
        },
        credentials: 'same-origin',
        redirect: 'manual',
        body: JSON.stringify(params)
      })
        .then((response) => {
          return response.json()
        })
        .then(async (comment) => {
          this.comments.push(comment)
          this.description = ''
          this.clearPreview('new-comment-preview')
          this.tab = 'comment'
          this.buttonDisabled = false
          this.resizeTextarea()
          this.displayToast(toastMessage)
          this.$store.dispatch('setWatchable', {
            watchableId: this.commentableId,
            watchableType: this.commentableType
          })
        })
        .catch((error) => {
          console.warn(error)
        })
        .finally(() => {
          Prism.highlightAll()
        })
    },
    deleteComment(id) {
      fetch(`/api/comments/${id}.json`, {
        method: 'DELETE',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': CSRF.getToken()
        },
        credentials: 'same-origin',
        redirect: 'manual'
      })
        .then(() => {
          this.comments.forEach((comment, i) => {
            if (comment.id === id) {
              this.comments.splice(i, 1)
            }
          })
        })
        .catch((error) => {
          console.warn(error)
        })
    },
    updateComment(description, id) {
      const updatedComment = this.comments.find((comment) => {
        return comment.id === id
      })
      updatedComment.description = description
    },
    setDefaultTextareaSize() {
      const textarea = document.getElementById('js-new-comment')
      if (textarea) {
        this.defaultTextareaSize = textarea.scrollHeight
      }
    },
    resizeTextarea() {
      const textarea = document.getElementById('js-new-comment')
      if (textarea) {
        textarea.style.height = `${this.defaultTextareaSize}px`
      }
    },
    commentAndCheck() {
      if (
        this.commentableType === 'Product' &&
        !window.confirm('提出物を修了済にしてよろしいですか？')
      ) {
        return null
      } else {
        this.createComment({ toastMessage: this.toastMessage() })
        this.check(
          this.commentableType,
          this.commentableId,
          '/api/checks',
          'POST',
          CSRF.getToken()
        )
      }
    },
    postComment() {
      if (this.commentableType === 'Report' && this.isRole('mentor')) {
        const notChecked = !this.checkId
        if (
          notChecked &&
          !window.confirm('日報を確認済みにしていませんがよろしいですか？')
        ) {
          return
        }
      }
      this.createComment()
      if (this.isUnassignedAndUnchekedProduct) {
        this.checkProduct(
          this.commentableId,
          this.currentUserId,
          '/api/products/checker',
          'PATCH',
          CSRF.getToken(),
          true
        )
      }
    },
    async fetchUncheckedProducts(page) {
      return fetch(`/api/products/unchecked?page=${page}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': CSRF.getToken()
        },
        credentials: 'same-origin',
        redirect: 'manual'
      })
        .then((response) => {
          return response.json()
        })
        .catch((error) => {
          console.warn(error)
          return null
        })
    },
    editComment() {
      if (this.description.length > 0) {
        this.editing = true
      }

      this.$nextTick(() => {
        Prism.highlightAll()
      })
    },
    clearPreview(elementId) {
      const parent = document.getElementById(elementId)
      while (parent.lastChild) {
        parent.removeChild(parent.lastChild)
      }
    },
    setTemplate(event) {
      // this.description にすでに値がある場合、確認ダイアログを表示
      if (this.description && !confirm(`内容をテンプレート（${event.target.textContent}）で上書きしますか？`)) {
        return; // 「いいえ」を選んだ場合は何もしない
      }

      const text = event.target.getAttribute("data-message")
      this.$refs.newComment.focus()
      this.$refs.newComment.value = text
      this.description = text

      // keyupイベントを発火させることで、textarea-markdownライブラリを発火
      const keyupEvent = new KeyboardEvent("keyup", {
        key: "Escape",
        keyCode: 27,  // 非推奨だが互換性のため
        code: "Escape",
        bubbles: false
      });

      this.$refs.newComment.dispatchEvent(keyupEvent);
    },
    // 課題から評価テーブルを抽出・加工する
    extractEvaluationTable() {
      if (!this.practice?.description) {
        return '課題の内容が設定されていません。';
      }
      const description = this.practice.description;
      const headingRegex = /^##\s*.*評価.*$/m;
      const match = description.match(headingRegex);
      if (!match) {
        return '評価基準が見つかりませんでした。';
      }

      const startIndex = match.index;
      let evaluationText = description.substring(startIndex);
      const endIndex = evaluationText.indexOf('\n---');
      if (endIndex !== -1) {
        evaluationText = evaluationText.substring(0, endIndex);
      }

      const lines = evaluationText.trim().split('\n');

      // テーブルの区切り行（|---|---|）を探す
      const separatorIndex = lines.findIndex(line => line.includes('---'));

      // 区切り行が見つからない場合はエラーメッセージを返す
      if (separatorIndex === -1) {
        return '評価基準内に有効なマークダウンテーブルが見つかりませんでした。';
      }

      const newLines = lines.map((line, index) => {
        const trimmedLine = line.trim();

        // テーブルの行ではないものは、そのまま返す (例: ## 評価基準)
        if (!trimmedLine.startsWith('|')) {
          return line;
        }

        // ヘッダー行 (区切り行の1つ上の行)
        if (index === separatorIndex - 1) {
          return trimmedLine + ' 結果 |';

        // 区切り行
        } else if (index === separatorIndex) {
          return trimmedLine + '-----|';

        // データ行 (区切り行より後の行)
        } else if (index > separatorIndex) {
          return trimmedLine + ' xx点 |';

        // ヘッダーより前の不正な行などはそのまま返す
        } else {
          return line;
        }
      });

      // 見出しを固定の文字列に置換する処理を追加
      const headingIndex = newLines.findIndex(line => line.match(headingRegex));
      if (headingIndex !== -1) {
        newLines[headingIndex] = '## 評価結果';
      }

      return newLines.join('\n');
    },

    // 「修正点あり」用のテンプレートを生成するメソッド
    generateCorrectionMessage() {
      const evaluationTable = this.extractEvaluationTable();
      if (!evaluationTable.startsWith('##')) {
        return evaluationTable;
      }
      const correctionsTemplate = `## 修正点（再提出必要）

- ファイル名：〇〇〇〇.cs    行番号：999
  - 修正点の内容を書く〇〇〇〇〇〇〇〇〇〇〇〇〇〇〇

## 改善点

- ファイル名：〇〇〇〇.cs    行番号：999
  - 改善点の内容を書く〇〇〇〇〇〇〇〇〇〇〇〇〇〇〇`;

      return `${evaluationTable}\n\n${correctionsTemplate}`;
    },

    // 「改善点あり」用のテンプレートを生成するメソッド
    generateEvaluationMessage() {
      const evaluationTable = this.extractEvaluationTable();
      // ヘルパーがエラーメッセージを返した場合は、それをそのまま使う
      if (!evaluationTable.startsWith('##')) {
        return evaluationTable;
      }
      const improvementsTemplate = `## 改善点

- ファイル名：〇〇〇〇.cs    行番号：999
  - 改善点の内容を書く〇〇〇〇〇〇〇〇〇〇〇〇〇〇〇`;

      return `${evaluationTable}\n\n${improvementsTemplate}`;
    },

    // 「改善点なし」用のテンプレートを生成するメソッド
    generatePerfectionMessage() {
      const evaluationTable = this.extractEvaluationTable();
      if (!evaluationTable.startsWith('##')) {
        return evaluationTable;
      }
      const noImprovementsTemplate = `## 改善点

- なし`;

      return `${evaluationTable}\n\n${noImprovementsTemplate}`;
    },
  }
}
</script>
